/**
 * useYjsSync - ReactFlow와 Yjs CRDT를 연결하는 커스텀 훅
 * 
 * 기존 Socket.IO 기반 broadcast/canvas-update를 대체합니다.
 * 
 * 기능:
 * - Y.Map 기반 노드/엣지 CRDT 동기화
 * - Y.UndoManager 기반 Undo/Redo (captureTimeout으로 자동 병합)
 * - Awareness 프로토콜로 유저 커서/선택/프레즌스 공유
 * - PostgreSQL 자동 영속화 (서버 측 debounce)
 * 
 * Undo 전략:
 * - trackHistory=true  → 'local' origin → UndoManager가 추적
 * - trackHistory=false → 'local-silent' origin → UndoManager가 무시
 * - captureTimeout(500ms) 내 연속 변경은 하나의 undo 스텝으로 자동 병합
 * - stopCapturing()으로 undo 스텝 경계를 명시적으로 제어 (드래그, 텍스트 편집 등)
 * - gc: false → UndoManager가 참조하는 CRDT 아이템이 GC되지 않도록 방지
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';

// WebSocket URL 결정 함수
// 빌드타임 상수(REACT_APP_YJS_WS_URL)를 쓰지 않는다 — 이미지를 dev/prd 공용으로 두기 위해
// 항상 현재 origin 에서 파생한다. ALB Ingress 가 /yjs 를 api 로 라우팅한다.
const getYjsWebSocketUrl = () => {
  // 로컬 개발 환경
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'ws://localhost:5000/yjs';
  }

  // 프로덕션 환경 (nginx 프록시를 통해 연결)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/yjs`;
};

/**
 * useYjsSync 훅
 * 
 * @param {Object} params
 * @param {string} params.canvasId - 캔버스 ID (형식: "${fileId}-${tabId}-${activeCanvasId}")
 * @param {Function} params.setNodes - ReactFlow setNodes 함수
 * @param {Function} params.setEdges - ReactFlow setEdges 함수  
 * @param {React.MutableRefObject} params.nodesRef - 노드 ref (최신 상태)
 * @param {React.MutableRefObject} params.edgesRef - 엣지 ref (최신 상태)
 * @param {Object} params.user - 현재 사용자 정보 { name, color }
 * @param {React.MutableRefObject} params.draggingNodeIdsRef - 드래그 중인 노드 ID들
 * @param {React.MutableRefObject} params.resizingNodeIdsRef - 리사이즈 중인 노드 ID들
 * @param {Function} params.generateThumbnail - 썸네일 생성 함수
 * @param {boolean} params.enabled - Yjs 활성화 여부 (기본값: true)
 */
export function useYjsSync({
  canvasId,
  setNodes,
  setEdges,
  nodesRef,
  edgesRef,
  user,
  draggingNodeIdsRef,
  resizingNodeIdsRef,
  generateThumbnail,
  enabled = true,
}) {
  const ydocRef = useRef(null);
  const providerRef = useRef(null);
  const undoManagerRef = useRef(null); // Y.UndoManager
  const isExternalUpdateRef = useRef(false);
  const thumbnailTimeoutRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState(1);
  const [userList, setUserList] = useState([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Awareness 상태
  const [awarenessStates, setAwarenessStates] = useState(new Map());

  // canvasId ref (최신 값 추적)
  const canvasIdRef = useRef(canvasId);
  useEffect(() => {
    canvasIdRef.current = canvasId;
  }, [canvasId]);

  // user ref
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  /**
   * Yjs 문서, Provider, UndoManager 초기화
   */
  useEffect(() => {
    if (!enabled || !canvasId || canvasId === 'default') {
      return;
    }

    // console.log(`🔌 [Yjs] 연결 시작: ${canvasId}`);
    const t0 = performance.now();
    // console.log(`⏱️ [로드] 시작: ${canvasId}`);

    // Y.Doc 생성
    // ⚡ gc: false - UndoManager가 참조하는 CRDT 아이템이 GC되지 않도록 방지
    const ydoc = new Y.Doc({ gc: false });
    ydocRef.current = ydoc;

    // Y.Map 구조 (노드/엣지)
    const yNodes = ydoc.getMap('nodes');
    const yEdges = ydoc.getMap('edges');

    // WebSocket Provider 연결
    const wsUrl = getYjsWebSocketUrl();
    const provider = new WebsocketProvider(wsUrl, canvasId, ydoc, {
      connect: true,
    });
    providerRef.current = provider;

    // ⚡ Y.UndoManager 초기화
    // trackedOrigins: 'local' origin만 추적 → 'local-silent'은 무시
    // captureTimeout: 500ms 이내 연속 변경은 자동 병합 (드래그, 타이핑 등)
    const undoManager = new Y.UndoManager([yNodes, yEdges], {
      trackedOrigins: new Set(['local']),
      captureTimeout: 500,
    });
    undoManagerRef.current = undoManager;

    // UndoManager 스택 변경 이벤트 리스너
    const updateUndoRedoState = () => {
      setCanUndo(undoManager.undoStack.length > 0);
      setCanRedo(undoManager.redoStack.length > 0);
    };
    undoManager.on('stack-item-added', updateUndoRedoState);
    undoManager.on('stack-item-popped', updateUndoRedoState);

    // =============================================
    // Yjs 노드 변경 관찰 → ReactFlow 상태 업데이트
    // =============================================
    // ⚡ 원격 Yjs 값을 로컬 상태와 병합하는 헬퍼
    const mergeRemoteNode = (key, value, localNode, currentDragging, currentResizing) => {
      // 드래그/리사이즈 중인 노드는 로컬 상태 유지
      if (currentDragging.has(key) || currentResizing.has(key)) {
        if (localNode) return localNode;
      }

      // 내가 편집 중인 텍스트박스는 로컬 상태 유지
      if (localNode && localNode.type === 'textbox' && localNode.data?.isEditing && !localNode.data?.editingBy) {
        return {
          ...localNode,
          data: {
            ...localNode.data,
            editingBy: value.data?.editingBy,
          }
        };
      }

      // 원격 데이터를 적용하되, 로컬 전용 상태(selected, width/height 등)는 보존
      if (localNode) {
        return {
          ...value,
          selected: localNode.selected || false,
          dragging: false,
          resizing: false,
          width: value.width ?? localNode.width,
          height: value.height ?? localNode.height,
          data: {
            ...value.data,
            selectedBy: localNode.selected ? localNode.data?.selectedBy : value.data?.selectedBy,
            showUserLabel: localNode.selected ? localNode.data?.showUserLabel : value.data?.showUserLabel,
          }
        };
      }

      return value;
    };

    const yNodesObserver = (event) => {
      // 로컬에서 syncNodes를 통해 업데이트한 경우, 이미 setNodes가 호출됨 → 스킵
      if (isExternalUpdateRef.current) return;

      const currentDragging = draggingNodeIdsRef?.current || new Set();
      const currentResizing = resizingNodeIdsRef?.current || new Set();

      // ⚡ event.changes.keys를 사용하여 변경된 노드만 delta 패치
      const changedKeys = event.changes.keys;
      let hasAddOrDelete = false;
      const updatedKeys = new Set();
      const deletedKeys = new Set();

      changedKeys.forEach((change, key) => {
        if (change.action === 'add') {
          hasAddOrDelete = true;
          updatedKeys.add(key);
        } else if (change.action === 'update') {
          updatedKeys.add(key);
        } else if (change.action === 'delete') {
          hasAddOrDelete = true;
          deletedKeys.add(key);
        }
      });

      // 삭제된 노드 제거
      let newNodes = deletedKeys.size > 0
        ? nodesRef.current.filter(n => !deletedKeys.has(n.id))
        : [...nodesRef.current];

      // 변경/추가된 노드 처리
      const localNodeMap = new Map();
      newNodes.forEach(n => localNodeMap.set(n.id, n));

      updatedKeys.forEach(key => {
        const value = yNodes.get(key);
        if (!value || typeof value !== 'object') return;

        const localNode = localNodeMap.get(key);
        const mergedNode = mergeRemoteNode(key, value, localNode, currentDragging, currentResizing);

        if (localNodeMap.has(key)) {
          // 기존 노드 교체 (인덱스를 유지하여 sort 최소화)
          const idx = newNodes.findIndex(n => n.id === key);
          if (idx !== -1) {
            newNodes[idx] = mergedNode;
          }
        } else {
          // 새 노드 추가
          newNodes.push(mergedNode);
        }
      });

      // 추가/삭제가 있을 때만 displayOrder 정렬 (update만이면 순서 불변)
      if (hasAddOrDelete) {
        newNodes.sort((a, b) => {
          const orderA = a.data?.displayOrder ?? 0;
          const orderB = b.data?.displayOrder ?? 0;
          return orderA - orderB;
        });
      }

      setNodes(newNodes);
      nodesRef.current = newNodes;
    };

    const yEdgesObserver = (event) => {
      if (isExternalUpdateRef.current) return;

      const newEdges = [];
      yEdges.forEach((value, key) => {
        if (value && typeof value === 'object') {
          newEdges.push(value);
        }
      });

      setEdges(newEdges);
      edgesRef.current = newEdges;
    };

    yNodes.observe(yNodesObserver);
    yEdges.observe(yEdgesObserver);

    // =============================================
    // Provider 이벤트
    // =============================================
    // Y.Map에서 노드/엣지를 읽어 ReactFlow에 반영하는 헬퍼
    const applyYMapsToReactFlow = () => {
      // 기존 로컬 노드에서 DOM 측정된 width/height 보존용 맵
      const localDims = new Map();
      nodesRef.current?.forEach(n => {
        if (n.width != null && n.height != null) {
          localDims.set(n.id, { width: n.width, height: n.height });
        }
      });

      const nodes = [];
      yNodes.forEach((value, key) => {
        if (value && typeof value === 'object') {
          const { selected, dragging, resizing, ...rest } = value;
          const node = { ...rest, selected: false, dragging: false };
          // Y.Map에 dimensions 없으면 로컬에서 복구
          if (node.width == null || node.height == null) {
            const dims = localDims.get(key);
            if (dims) {
              node.width = node.width ?? dims.width;
              node.height = node.height ?? dims.height;
            }
          }
          nodes.push(node);
        }
      });

      // displayOrder로 정렬
      nodes.sort((a, b) => {
        const orderA = a.data?.displayOrder ?? 0;
        const orderB = b.data?.displayOrder ?? 0;
        return orderA - orderB;
      });

      const edges = [];
      yEdges.forEach((value, key) => {
        if (value && typeof value === 'object') {
          edges.push(value);
        }
      });

      setNodes(nodes);
      setEdges(edges);
      nodesRef.current = nodes;
      edgesRef.current = edges;
      return nodes.length;
    };

    let retrySyncTimer = null;

    provider.on('sync', (synced) => {
      console.log(`🔄 [Yjs] 동기화 상태: ${synced ? '완료' : '진행 중'} (${canvasId})`);
      setIsSynced(synced);

      if (synced) {
        // console.log(`⏱️ [로드] Yjs sync 완료: ${(performance.now() - t0).toFixed(0)}ms (노드 ${yNodes.size}개)`);
        // 초기 동기화 완료 시 노드/엣지를 ReactFlow에 반영
        const t1 = performance.now();
        const nodeCount = applyYMapsToReactFlow();
        // console.log(`⏱️ [로드] applyYMapsToReactFlow(setNodes): ${(performance.now() - t1).toFixed(0)}ms`);
        setIsReady(true);
        const tReady = performance.now();
        requestAnimationFrame(() => {
          // console.log(`⏱️ [로드] 첫 렌더 프레임: ${(performance.now() - tReady).toFixed(0)}ms`);
          requestAnimationFrame(() => {
            // console.log(`⏱️ [로드] 두번째 렌더 프레임: ${(performance.now() - tReady).toFixed(0)}ms`);
            if (typeof requestIdleCallback !== 'undefined') {
              requestIdleCallback(() => {
                // console.log(`⏱️ [로드] 브라우저 idle (스크롤 가능): ${(performance.now() - tReady).toFixed(0)}ms`);
              });
            }
          });
        });

        // 🔧 안전장치: sync 완료됐지만 데이터가 비어있으면 재시도
        if (nodeCount === 0) {
          console.log(`⚠️ [Yjs] 동기화 완료됐지만 노드 없음, 재시도 예약 (${canvasId})`);
          retrySyncTimer = setTimeout(() => {
            if (yNodes.size === 0 && provider.wsconnected) {
              console.log(`🔄 [Yjs] 빈 데이터 재동기화 시도 (${canvasId})`);
              provider.disconnect();
              provider.connect();
            }
          }, 2000);
        }
      }
    });

    provider.on('status', ({ status }) => {
      console.log(`📡 [Yjs] 연결 상태: ${status} (${canvasId})`);
    });

    // =============================================
    // Awareness (유저 프레즌스)
    // =============================================
    const awareness = provider.awareness;

    // 자신의 Awareness 상태 설정
    if (user) {
      awareness.setLocalStateField('user', {
        name: user.name || 'Anonymous',
        color: user.color || getRandomColor(),
        cursor: null,
        selectedNodes: [],
        viewport: null,
      });
    }

    // Awareness 변경 리스너
    const onAwarenessChange = () => {
      const states = awareness.getStates();
      const userMap = new Map(); // name → {name, clientId, color} (첫 번째 매칭 유지)
      const statesMap = new Map();

      states.forEach((state, clientId) => {
        if (state.user) {
          if (!userMap.has(state.user.name)) {
            userMap.set(state.user.name, {
              name: state.user.name,
              clientId,
              color: state.user.color,
            });
          }
          statesMap.set(clientId, state);
        }
      });

      const uniqueUserList = [...userMap.values()];
      setConnectedUsers(uniqueUserList.length);
      setUserList(uniqueUserList);
      setAwarenessStates(statesMap);
    };

    awareness.on('change', onAwarenessChange);
    // 초기 상태 업데이트
    onAwarenessChange();

    // =============================================
    // 정리 (Cleanup)
    // =============================================
    return () => {
      console.log(`🔌 [Yjs] 연결 해제: ${canvasId}`);

      // 재시도 타이머 정리
      if (retrySyncTimer) {
        clearTimeout(retrySyncTimer);
        retrySyncTimer = null;
      }

      // 리스너 제거
      yNodes.unobserve(yNodesObserver);
      yEdges.unobserve(yEdgesObserver);
      awareness.off('change', onAwarenessChange);

      // UndoManager 정리
      undoManager.off('stack-item-added', updateUndoRedoState);
      undoManager.off('stack-item-popped', updateUndoRedoState);
      undoManager.destroy();
      undoManagerRef.current = null;

      // Provider 연결 해제
      provider.disconnect();
      provider.destroy();
      providerRef.current = null;

      // Y.Doc 정리
      ydoc.destroy();
      ydocRef.current = null;

      // 상태 초기화
      setIsReady(false);
      setIsSynced(false);
      setCanUndo(false);
      setCanRedo(false);

      // 썸네일 타이머 정리
      if (thumbnailTimeoutRef.current) {
        clearTimeout(thumbnailTimeoutRef.current);
      }
    };
  }, [canvasId, enabled]); // canvasId 변경 시 새 문서 연결

  // =============================================
  // API: beginUndoCapture / commitUndoCapture
  // Y.UndoManager의 stopCapturing()을 래핑하여 undo 스텝 경계를 제어합니다.
  //
  // 사용 패턴 (드래그, 텍스트 편집 등):
  //   1. beginUndoCapture() → stopCapturing() → 이전 작업과 분리
  //   2. syncNodes(trackHistory: true) 반복 → captureTimeout으로 자동 병합
  //   3. commitUndoCapture() → stopCapturing() → 다음 작업과 분리
  // =============================================

  /**
   * 다단계 작업 시작 시 호출: UndoManager의 현재 캡처를 중단하여
   * 이후 변경이 새로운 undo 스텝으로 기록되도록 합니다.
   * 
   * @param {Array<string>} [nodeIds] - (참고용) 변경될 노드 ID 배열
   */
  const beginUndoCapture = useCallback((nodeIds) => {
    const um = undoManagerRef.current;
    if (um) {
      um.stopCapturing();
      console.log(`🔧 [UndoManager] stopCapturing (begin): ${nodeIds?.length || 0}개 노드 작업 시작`);
    }
  }, []);

  /**
   * 다단계 작업 종료 시 호출: UndoManager의 현재 캡처를 중단하여
   * 이후 변경이 별도의 undo 스텝으로 기록되도록 합니다.
   */
  const commitUndoCapture = useCallback(() => {
    const um = undoManagerRef.current;
    if (um) {
      um.stopCapturing();
      console.log('🔧 [UndoManager] stopCapturing (commit): 작업 종료');
    }
  }, []);

  // =============================================
  // API: syncNodes - 노드 변경사항을 Yjs에 동기화
  // =============================================
  /**
   * 노드 변경사항을 Yjs 문서에 동기화합니다.
   * 기존 broadcast() 함수를 대체합니다.
   * 
   * @param {Array} newNodes - 전체 노드 배열 (최신 상태)
   * @param {Object} options
   * @param {Array|null} options.updatedNodeIds - 변경된 노드 ID 배열 (null이면 전체 업데이트)
   * @param {Array|null} options.deletedNodeIds - 삭제된 노드 ID 배열
   * @param {boolean} options.triggerThumbnail - 썸네일 생성 트리거 여부
   * @param {boolean} options.trackHistory - Undo 히스토리 추적 여부 (기본값: true)
   *   true  → 'local' origin → UndoManager가 추적
   *   false → 'local-silent' origin → UndoManager가 무시
   */
  const syncNodes = useCallback((newNodes, options = {}) => {
    const {
      updatedNodeIds = null,
      deletedNodeIds = null,
      triggerThumbnail = true,
      trackHistory = true,
    } = options;

    const ydoc = ydocRef.current;
    if (!ydoc) return;

    const yNodes = ydoc.getMap('nodes');

    // ⚡ trackHistory에 따라 트랜잭션 origin 결정
    // 'local' → UndoManager가 추적 (undo 가능)
    // 'local-silent' → UndoManager가 무시 (선택 변경 등 UI 상태)
    const txOrigin = trackHistory ? 'local' : 'local-silent';

    // 노드에서 selected 제거 + textbox isEditing 처리 헬퍼
    // ⚡ selected는 ReactFlow 로컬 전용이므로 Y.Map에 저장하지 않음
    // ⚡ selectedBy/showUserLabel은 유지 (다른 브라우저에서 선택 표시에 사용)
    const cleanNodeForSync = (node) => {
      const { selected, dragging, resizing, ...cleaned } = node;
      if (node.type === 'textbox' && cleaned.data) {
        cleaned.data = {
          ...cleaned.data,
          isEditing: false,
        };
      }
      return cleaned;
    };

    // 두 노드가 동일한지 비교 (shallow 우선 → data만 JSON 비교)
    // UndoManager가 변경되지 않은 노드까지 추적하는 것을 방지
    // ⚡ position, width, height, type 등 자주 변경되는 필드를 먼저 비교하여
    //    동일하면 비싼 JSON.stringify를 건너뜀
    const hasNodeChanged = (existing, newNode) => {
      if (!existing || !newNode) return true;
      // 위치 비교
      if (existing.position?.x !== newNode.position?.x ||
          existing.position?.y !== newNode.position?.y) return true;
      // 크기 비교
      if (existing.width !== newNode.width || existing.height !== newNode.height) return true;
      // 타입 비교
      if (existing.type !== newNode.type) return true;
      // 부모 노드 비교 (그룹 이동 등)
      if (existing.parentId !== newNode.parentId) return true;
      // z-index 비교 (레이어 순서)
      if (existing.zIndex !== newNode.zIndex) return true;
      // 숨김 여부 비교
      if (existing.hidden !== newNode.hidden) return true;
      // 최상위 style 비교 (노드 스타일이 data 외에 style 프롭으로 지정된 경우)
      if (JSON.stringify(existing.style) !== JSON.stringify(newNode.style)) return true;
      // data 비교 (transient 필드 제외) - data만 JSON.stringify
      try {
        const stripData = (d) => {
          if (!d) return d;
          const { selectedBy, showUserLabel, ...rest } = d;
          return rest;
        };
        return JSON.stringify(stripData(existing.data)) !== JSON.stringify(stripData(newNode.data));
      } catch {
        return true;
      }
    };

    // 외부 업데이트 플래그 설정 (observer에서 중복 처리 방지)
    isExternalUpdateRef.current = true;

    try {
      ydoc.transact(() => {
        if (deletedNodeIds && deletedNodeIds.length > 0) {
          // 삭제할 노드만 제거
          deletedNodeIds.forEach(nodeId => {
            if (yNodes.has(nodeId)) {
              yNodes.delete(nodeId);
            }
          });
        }

        if (updatedNodeIds && Array.isArray(updatedNodeIds) && updatedNodeIds.length > 0) {
          // 부분 업데이트: 변경된 노드만 업데이트
          // ⚡ Map으로 O(1) 조회 (기존 find는 O(n))
          const nodeMap = new Map(newNodes.map(n => [n.id, n]));
          let partialSkipped = 0;
          updatedNodeIds.forEach(nodeId => {
            const node = nodeMap.get(nodeId);
            if (node) {
              const cleaned = cleanNodeForSync(node);
              const existing = yNodes.get(nodeId);
              // 변경되지 않은 노드는 건너뛰기 → CRDT 아이템 교체 방지
              if (existing && !hasNodeChanged(existing, cleaned)) {
                partialSkipped++;
                return;
              }
              yNodes.set(nodeId, cleaned);
            }
          });
          if (partialSkipped > 0) {
            // console.log(`🔧 [syncNodes] 부분 업데이트: ${partialSkipped}개 노드 변경 없어 건너뜀 (UndoManager 보호)`);
          }
        } else if (updatedNodeIds === null && !deletedNodeIds) {
          // 전체 업데이트: 모든 노드 동기화
          // ⚡ 중요: 변경되지 않은 노드는 yNodes.set()을 건너뛰어
          //    UndoManager가 다른 사용자의 변경사항까지 추적하지 않도록 합니다.
          const newNodeIds = new Set(newNodes.map(n => n.id));

          // 삭제된 노드 제거
          const keysToDelete = [];
          yNodes.forEach((_, key) => {
            if (!newNodeIds.has(key)) {
              keysToDelete.push(key);
            }
          });
          keysToDelete.forEach(key => yNodes.delete(key));

          // 노드 추가/업데이트 (변경된 것만)
          let skippedCount = 0;
          newNodes.forEach(node => {
            const cleaned = cleanNodeForSync(node);
            const existing = yNodes.get(node.id);

            // 이미 Y.Map에 같은 값이 있으면 건너뛰기
            // → UndoManager가 이 노드를 추적하지 않음
            if (existing && !hasNodeChanged(existing, cleaned)) {
              skippedCount++;
              return;
            }

            yNodes.set(node.id, cleaned);
          });

          if (skippedCount > 0) {
            console.log(`🔧 [syncNodes] 전체 업데이트: ${skippedCount}개 노드 변경 없어 건너뜀`);
          }
        }
      }, txOrigin);
    } finally {
      // 트랜잭션 완료 후 즉시 플래그 해제
      // (Y.Map observer는 transact() 내에서 동기적으로 실행됨)
      isExternalUpdateRef.current = false;
    }

    // 썸네일 업데이트 (3초 debounce)
    if (triggerThumbnail && generateThumbnail) {
      if (thumbnailTimeoutRef.current) {
        clearTimeout(thumbnailTimeoutRef.current);
      }
      thumbnailTimeoutRef.current = setTimeout(() => {
        generateThumbnail();
      }, 3000);
    }
  }, [generateThumbnail]);

  // =============================================
  // API: syncEdges - 엣지 변경사항을 Yjs에 동기화
  // =============================================
  const syncEdges = useCallback((newEdges, options = {}) => {
    const { trackHistory = true } = options;
    const ydoc = ydocRef.current;
    if (!ydoc) return;

    const yEdges = ydoc.getMap('edges');

    // ⚡ trackHistory에 따라 트랜잭션 origin 결정
    const txOrigin = trackHistory ? 'local' : 'local-silent';

    isExternalUpdateRef.current = true;

    try {
      ydoc.transact(() => {
        const newEdgeIds = new Set(newEdges.map(e => e.id));

        // 삭제된 엣지 제거
        const keysToDelete = [];
        yEdges.forEach((_, key) => {
          if (!newEdgeIds.has(key)) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach(key => yEdges.delete(key));

        // 엣지 추가/업데이트
        newEdges.forEach(edge => {
          yEdges.set(edge.id, edge);
        });
      }, txOrigin);
    } finally {
      isExternalUpdateRef.current = false;
    }
  }, []);

  // =============================================
  // API: Undo/Redo (Y.UndoManager 기반)
  // =============================================

  /**
   * Y.Map에서 현재 노드/엣지를 읽어 React 상태 강제 동기화
   * ⚡ 로컬 전용 UI 상태 (selected, selectedBy, showUserLabel)를 보존하여
   *    Undo/Redo 후 선택이 해제되는 문제 방지
   */
  const forceStateFromYMap = useCallback(() => {
    const ydoc = ydocRef.current;
    if (!ydoc) return;

    const yNodes = ydoc.getMap('nodes');
    const yEdges = ydoc.getMap('edges');

    // 로컬 노드를 빠르게 조회하기 위한 Map
    const localNodeMap = new Map();
    nodesRef.current.forEach(n => localNodeMap.set(n.id, n));

    const newNodes = [];
    yNodes.forEach((value, key) => {
      if (value && typeof value === 'object') {
        const localNode = localNodeMap.get(key);
        if (localNode) {
          // ⚡ Y.Map 데이터에 로컬 전용 상태 보존 (width/height 포함)
          newNodes.push({
            ...value,
            selected: localNode.selected || false,
            dragging: false,
            resizing: false,
            width: value.width ?? localNode.width,
            height: value.height ?? localNode.height,
            data: {
              ...value.data,
              selectedBy: localNode.data?.selectedBy,
              showUserLabel: localNode.data?.showUserLabel,
            }
          });
        } else {
          const { dragging, resizing, ...cleaned } = value;
          newNodes.push({ ...cleaned, dragging: false });
        }
      }
    });
    newNodes.sort((a, b) => {
      const orderA = a.data?.displayOrder ?? 0;
      const orderB = b.data?.displayOrder ?? 0;
      return orderA - orderB;
    });

    const newEdges = [];
    yEdges.forEach((value, key) => {
      if (value && typeof value === 'object') {
        newEdges.push(value);
      }
    });

    setNodes(newNodes);
    nodesRef.current = newNodes;
    setEdges(newEdges);
    edgesRef.current = newEdges;
  }, [setNodes, setEdges, nodesRef, edgesRef]);

  /**
   * Undo - Y.UndoManager를 사용하여 마지막 로컬 변경을 되돌립니다.
   */
  const undo = useCallback(() => {
    const um = undoManagerRef.current;
    if (!um || um.undoStack.length === 0) {
      console.log('↩️ [UndoManager] Undo 스택 비어있음');
      return;
    }

    console.log(`↩️ [UndoManager] Undo 실행 (스택: ${um.undoStack.length}개)`);

    isExternalUpdateRef.current = true;
    try {
      um.undo();
    } finally {
      isExternalUpdateRef.current = false;
    }

    // Y.Map 변경 후 React 상태 강제 반영
    forceStateFromYMap();
    console.log(`↩️ [UndoManager] Undo 완료 (남은 undo: ${um.undoStack.length}개, redo: ${um.redoStack.length}개)`);
  }, [forceStateFromYMap]);

  /**
   * Redo - Y.UndoManager를 사용하여 되돌린 변경을 다시 적용합니다.
   */
  const redo = useCallback(() => {
    const um = undoManagerRef.current;
    if (!um || um.redoStack.length === 0) {
      console.log('↪️ [UndoManager] Redo 스택 비어있음');
      return;
    }

    console.log(`↪️ [UndoManager] Redo 실행 (스택: ${um.redoStack.length}개)`);

    isExternalUpdateRef.current = true;
    try {
      um.redo();
    } finally {
      isExternalUpdateRef.current = false;
    }

    // Y.Map 변경 후 React 상태 강제 반영
    forceStateFromYMap();
    console.log(`↪️ [UndoManager] Redo 완료 (남은 undo: ${um.undoStack.length}개, redo: ${um.redoStack.length}개)`);
  }, [forceStateFromYMap]);

  // =============================================
  // API: Awareness 업데이트
  // =============================================
  const updateAwareness = useCallback((field, value) => {
    const provider = providerRef.current;
    if (!provider) return;

    provider.awareness.setLocalStateField(field, value);
  }, []);

  /**
   * 선택된 노드 업데이트 (Awareness)
   */
  const updateSelectedNodes = useCallback((selectedNodeIds) => {
    updateAwareness('user', {
      ...userRef.current,
      selectedNodes: selectedNodeIds,
    });
  }, [updateAwareness]);

  /**
   * 커서 위치 업데이트 (Awareness)
   */
  const updateCursor = useCallback((cursor) => {
    const provider = providerRef.current;
    if (!provider) return;

    const currentState = provider.awareness.getLocalState();
    if (currentState?.user) {
      provider.awareness.setLocalStateField('user', {
        ...currentState.user,
        cursor,
      });
    }
  }, []);

  /**
   * 뷰포트 업데이트 (Awareness)
   */
  const updateViewport = useCallback((viewport) => {
    const provider = providerRef.current;
    if (!provider) return;

    const currentState = provider.awareness.getLocalState();
    if (currentState?.user) {
      provider.awareness.setLocalStateField('user', {
        ...currentState.user,
        viewport,
      });
    }
  }, []);

  return {
    // 상태
    isReady,
    isSynced,
    connectedUsers,
    userList,
    canUndo,
    canRedo,
    awarenessStates,

    // 동기화 API (broadcast 대체)
    syncNodes,
    syncEdges,

    // Undo/Redo (Y.UndoManager 기반)
    undo,
    redo,

    // 다단계 Undo 캡처 (드래그/리사이즈/텍스트 편집용)
    beginUndoCapture,
    commitUndoCapture,

    // Awareness API
    updateAwareness,
    updateSelectedNodes,
    updateCursor,
    updateViewport,

    // Refs (직접 접근 필요 시)
    ydocRef,
    providerRef,
    undoManagerRef,
  };
}

// 유틸리티: 랜덤 색상 생성
function getRandomColor() {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
    '#F8C471', '#82E0AA', '#F1948A', '#85929E', '#73C6B6',
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

export default useYjsSync;
